import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import Toasts from "../../../core/common/toast";
import * as Yup from "yup";
import {
  addMasterBeacon,
  findMasterBeacon,
  updateMasterBeacon,
} from "../../../core/data/redux/masterBeaconSlice";
import { all_routes } from "../../router/all_routes";

interface AddMasterBeaconProps { }

const schema = Yup.object().shape({
  uuid: Yup.string()
    .required("UUID is required")
    .max(100, "UUID must be at most 100 characters"),
  major: Yup.number()
    .required("Major is required")
    .min(0, "Major must be a positive number")
    .max(65535, "Major must be at most 65535"),
  minor: Yup.number()
    .required("Minor is required")
    .min(0, "Minor must be a positive number")
    .max(65535, "Minor must be at most 65535"),
  beaconName: Yup.string()
    .max(100, "Beacon name must be at most 100 characters"),
  locationDescription: Yup.string()
    .max(500, "Location description must be at most 500 characters"),
  status: Yup.string()
    .oneOf(['active', 'inactive'], "Status must be either 'active' or 'inactive'")
    .required("Status is required"),
});

const AddMasterBeacon = (props: AddMasterBeaconProps) => {
  const { id } = useParams();
  const navigation = useNavigate();
  const routes = all_routes;
  const dispatch = useDispatch();
  const masterBeaconState = useSelector(
    (state: any) => state.masterBeaconState
  );
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      uuid: "",
      major: 0,
      minor: 0,
      beaconName: "",
      locationDescription: "",
      status: "active",
    },
  });

  React.useEffect(() => {
    if (id) {
      dispatch(findMasterBeacon({ id: id }));
    }
  }, [id]);

  const onSubmit = (data: any) => {
    if (id) {
      dispatch(updateMasterBeacon({ id, ...data }));
    } else {
      dispatch(addMasterBeacon(data));
    }
  };


  React.useEffect(() => {
    if (
      masterBeaconState.loading === "fulfilled" &&
      masterBeaconState.action === "add" || masterBeaconState.action === "update"
    ) {
      navigation(routes.masterBeacon.list);
    } else if (
      masterBeaconState.loading === "fulfilled" &&
      masterBeaconState.action === "find"
    ) {
      const result = masterBeaconState?.data || null;
      if (result) {
        setValue("uuid", result.uuid);
        setValue("major", result.major);
        setValue("minor", result.minor);
        setValue("beaconName", result.beaconName || "");
        setValue("locationDescription", result.locationDescription || "");
        setValue("status", result.status);
      }
    }
  }, [masterBeaconState.loading, masterBeaconState.action]);

  return (
    <div>
      <Toasts
        show={masterBeaconState.loading === "rejected"}
        message={masterBeaconState.error}
        title={"Failed"}
        type={"danger"}
      />

      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {id ? "Edit Master Beacon" : "Add Master Beacon"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.masterBeacon.list}>Master Beacons</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {id ? "Edit Beacon" : "Add Beacon"}
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">
                {id ? "Edit Master Beacon" : "Add Master Beacon"}
              </h4>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">UUID *</label>
                      <input
                        type="text"
                        className={`form-control ${errors.uuid ? "is-invalid" : ""}`}
                        placeholder="Enter beacon UUID"
                        {...register("uuid")}
                      />
                      {errors.uuid && (
                        <div className="invalid-feedback">
                          {errors.uuid.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Major *</label>
                      <input
                        type="number"
                        className={`form-control ${errors.major ? "is-invalid" : ""}`}
                        placeholder="0-65535"
                        min="0"
                        max="65535"
                        {...register("major")}
                      />
                      {errors.major && (
                        <div className="invalid-feedback">
                          {errors.major.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="mb-3">
                      <label className="form-label">Minor *</label>
                      <input
                        type="number"
                        className={`form-control ${errors.minor ? "is-invalid" : ""}`}
                        placeholder="0-65535"
                        min="0"
                        max="65535"
                        {...register("minor")}
                      />
                      {errors.minor && (
                        <div className="invalid-feedback">
                          {errors.minor.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Beacon Name</label>
                      <input
                        type="text"
                        className={`form-control ${errors.beaconName ? "is-invalid" : ""}`}
                        placeholder="Enter beacon name (optional)"
                        {...register("beaconName")}
                      />
                      {errors.beaconName && (
                        <div className="invalid-feedback">
                          {errors.beaconName.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Status *</label>
                      <select
                        className={`form-control ${errors.status ? "is-invalid" : ""}`}
                        {...register("status")}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      {errors.status && (
                        <div className="invalid-feedback">
                          {errors.status.message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Location Description</label>
                      <textarea
                        className={`form-control ${errors.locationDescription ? "is-invalid" : ""}`}
                        rows={3}
                        placeholder="Enter location description (optional)"
                        {...register("locationDescription")}
                      />
                      {errors.locationDescription && (
                        <div className="invalid-feedback">
                          {errors.locationDescription.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <Link
                    to={routes.masterBeacon.list}
                    className="btn btn-light"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={masterBeaconState.loading === "pending"}
                  >
                    {masterBeaconState.loading === "pending" ? (
                      <span className="spinner-border spinner-border-sm me-2" />
                    ) : null}
                    {id ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMasterBeacon;
